import React, { useState } from 'react';
import { Stack, Box, IconButton, Menu, MenuItem, Checkbox, Button, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ClearIcon from '@mui/icons-material/Clear';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useColumnControl } from '@/context/columnControlContext.jsx';
import { usePipelineContext } from '@/context/pipelineContext';
import { useKanbanContext } from '@/context/kanbanContext';
import { useTableSort } from '@/context/tableSortContext';
import { resetFilters } from '@/features/table/instantSearchSlice';
import DealTableFilters from '@/components/tables/DealTable/DealTableFilters';
import ClientTableFilters from '@/components/tables/ClientTable/ClientTableFilters';
import { KanbanSortSelect } from '../KanbanSortSelect';
import DebtorsTableFilters from '@/components/tables/DebtorsTable/DebtorsTableFilters';
import { FilterAltOffOutlined } from '@mui/icons-material';

export const KanbanFiltersRow = () => {
	const { pipelineId, debtorFilters, isProspect } = useKanbanContext();

	const isDebtors = pipelineId === process.env.REACT_APP_PIPELINE_DEBTORS;
	const { clearSort } = useTableSort();

	const dispatch = useDispatch();
	const { pathname: slug } = useLocation();

	const handleClearFilters = () => {
		dispatch(resetFilters(slug));
		clearSort();
	};

	return (
		<Stack direction='column' width='100%' pb={2} px={3}>
			{isProspect ? (
				<ClientTableFilters tab='active' hideStageFilter />
			) : isDebtors ? (
				<DebtorsTableFilters
					{...{
						...debtorFilters,
						hideStageFilter: true,
					}}
				/>
			) : (
				<DealTableFilters pipeline={pipelineId} />
			)}
			<Stack
				direction='row'
				alignItems='flex-end'
				justifyContent='flex-end'
				flexWrap='wrap'
				spacing={1}
			>
				<KanbanSortSelect />
				{/* <Button
					sx={{ minHeight: 40, borderRadius: '6px', whiteSpace: 'nowrap' }}
					variant='text'
					size='small'
					startIcon={<ClearIcon />}
					onClick={handleClearFilters}
				>
					Clear filters
				</Button> */}
				<Tooltip title='Clear filters'>
					<IconButton
						// sx={{ width: '100%', minHeight: '40px', borderRadius: '6px' }}
						variant='text'
						size='small'
						color='primary'
						onClick={handleClearFilters}
					>
						<FilterAltOffOutlined />
					</IconButton>
				</Tooltip>
				<KanbanColumnVisibilityMenu />
			</Stack>
		</Stack>
	);
};

const KanbanColumnVisibilityMenu = () => {
	const { pipelineId } = useKanbanContext();
	const { stages } = usePipelineContext(pipelineId);

	const { hiddenColumns, toggleColumn, showAllColumns, hideColumns } = useColumnControl();

	// State for board options menu
	const [boardMenuAnchorEl, setBoardMenuAnchorEl] = useState(null);
	const boardMenuOpen = Boolean(boardMenuAnchorEl);

	const handleBoardMenuClick = (event) => {
		setBoardMenuAnchorEl(event.currentTarget);
	};

	const handleBoardMenuClose = () => {
		setBoardMenuAnchorEl(null);
	};

	const handleToggleColumn = (stageId) => {
		toggleColumn(stageId);
	};

	const handleShowAll = () => {
		showAllColumns();
		handleBoardMenuClose();
	};

	const hideAllColumns = () => {
		const allStageIds = stages?.map((stage) => stage.id) || [];
		hideColumns(allStageIds);
	};

	const handleHideAll = () => {
		hideAllColumns();
		handleBoardMenuClose();
	};

	return (
		<>
			{stages && stages.length > 0 && (
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						height: 40,
					}}
				>
					<IconButton
						size='small'
						onClick={handleBoardMenuClick}
						sx={(theme) => ({ color: theme.palette.text.secondary })}
					>
						<MoreVertIcon />
					</IconButton>

					<Menu
						anchorEl={boardMenuAnchorEl}
						open={boardMenuOpen}
						onClose={handleBoardMenuClose}
						PaperProps={{
							sx: {
								maxHeight: 400,
							},
						}}
					>
						<MenuItem
							disabled
							sx={(theme) => ({
								fontSize: '12px',
								fontWeight: 600,
								borderBottom: `1px solid ${theme.palette.divider}`,
							})}
						>
							Show/Hide Columns
						</MenuItem>

						{/* Batch actions */}
						<Box
							sx={(theme) => ({
								px: 1.5,
								py: 0.5,
								borderBottom: `1px solid ${theme.palette.divider}`,
							})}
						>
							<Button
								size='small'
								onClick={handleShowAll}
								sx={{ fontSize: '11px', textTransform: 'none', mr: 1 }}
							>
								Show All
							</Button>
							<Button
								size='small'
								onClick={handleHideAll}
								sx={{ fontSize: '11px', textTransform: 'none' }}
							>
								Hide All
							</Button>
						</Box>

						{/* Column list with checkboxes */}
						{stages?.map((stage) => {
							const isHidden = hiddenColumns?.includes(stage.id);
							return (
								<MenuItem
									key={stage.id}
									onClick={() => handleToggleColumn(stage.id)}
									sx={{ fontSize: '12px', py: 0.5 }}
									dense
								>
									<Checkbox
										checked={!isHidden}
										size='small'
										sx={{ p: 0.5, mr: 1 }}
									/>
									{stage.label}
								</MenuItem>
							);
						})}
					</Menu>
				</Box>
			)}
		</>
	);
};
